import React from "react";
import { RouteObject } from "react-router";
import { ApplicationStoreType } from "modules/root/models/store";
import { ProtectedRoute } from "modules/common/components/routing/ProtectedRoute";
import { routes } from "modules/common/routes";
import { DictionariesTabsPage } from "modules/dictionaries/components/DictionariesTabsPage";
import WorkTypesPage from "./work-types/components/WorkTypesPage";
import OrderTypesPage from "modules/dictionaries/order-types/components/OrderTypesPage";
import OrderStatusesPage from "modules/dictionaries/order-statuses/components/OrderStatusPage";
import ProductionStagesPage from "modules/dictionaries/production-stages/components/ProductionStagesPage";
import IpdTypesPage from "modules/dictionaries/ipd-types/components/IpdTypesPage";
import functionalities from "./functionalities";
import { canAny, can } from "modules/session/auth/access";
import { DeprGroupsPage } from "./inventory/components/DeprGroupPage";
import { CategoriesPage } from "./inventory/components/CategoriesPage";
import OrderIndicatorsPage from "./order-indicators/components/OrderIndicatorsPage";
import { IndicatorsPage } from "./order-indicators/components/IndicatorsPage";
import AgentsCategoriesPage from "./agents-categories/components/AgentsCategoriesPage";
import OverheadTypesPage from "./overhead-types/components/OverheadTypesPage";

const READ = Object.values(functionalities).filter((func) => func.endsWith("_READ"));

/**
 * «Справочники». Страницы «Группы амортизации» и «ТЭП» лежат по адресам внутри
 * /dictionaries, и в react-router 5 рендерились вместе со страницей вкладок —
 * маршруты совпадали оба сразу. Шестая версия выбирает один, самый точный,
 * поэтому те же две страницы описаны вложенными: страница вкладок рисует их
 * через `<Outlet>` и остаётся на месте, как было.
 */
export const buildRoutes = ({ store }: RoutesProps): RouteObject[] => {
  const { loggedIn, access } = store.session;
  const visible = canAny(READ, access);

  return [
    {
      path: `${routes.dictionaries.path}/*`,
      element: (
        <ProtectedRoute loggedIn={loggedIn}>
          {visible ? (
            <DictionariesTabsPage
              orderTypesPanel={
                can(functionalities.ORDERTYPE_READ, access) ? (
                  <OrderTypesPage store={store.orderTypes} access={access} />
                ) : null
              }
              workTypesPanel={
                can(functionalities.WORKTYPE_READ, access) ? (
                  <WorkTypesPage store={store.workTypes} access={access} />
                ) : null
              }
              orderStatusesPanel={
                can(functionalities.ORDERSTATUS_READ, access) ? (
                  <OrderStatusesPage store={store.orderStatuses} access={access} />
                ) : null
              }
              productionStagesPanel={
                can(functionalities.PRODUCTIONSTAGE_READ, access) ? (
                  <ProductionStagesPage store={store.productionStages} access={access} />
                ) : null
              }
              ipdTypesPanel={
                can(functionalities.IPDTYPE_READ, access) ? (
                  <IpdTypesPage store={store.ipdTypes} access={access} />
                ) : null
              }
              agentsCategoriesPanel={
                can(functionalities.AGENTSCATEGORIES_READ, access) ? (
                  <AgentsCategoriesPage store={store.agentsCategories} access={access} />
                ) : null
              }
              orderIndicatorsPanel={
                can(functionalities.ORDERINDICATOR_READ, access) ? (
                  <OrderIndicatorsPage store={store.orderIndicators} indicators={store.indicators} access={access} />
                ) : null
              }
              inventoryPanel={
                can(functionalities.INVENTORY_READ, access) ? (
                  <CategoriesPage store={store.inventoryItems} categories={store.categories} access={access} />
                ) : null
              }
              overheadTypesPanel={
                can(functionalities.OVERHEADTYPE_READ, access) ? (
                  <OverheadTypesPage store={store.overheadTypes} access={access} />
                ) : null
              }
            />
          ) : null}
        </ProtectedRoute>
      ),
      children: [
        {
          path: routes.dictionaries.inventory.pathGroups.replace(`${routes.dictionaries.path}/`, "") + "/*",
          element: can(functionalities.INVENTORY_READ, access) ? <DeprGroupsPage access={access} /> : null,
        },
        {
          path: routes.dictionaries.orderIndicator.pathIndicators.replace(`${routes.dictionaries.path}/`, "") + "/*",
          element: can(functionalities.ORDERINDICATOR_READ, access) ? <IndicatorsPage access={access} /> : null,
        },
      ],
    },
  ];
};

interface RoutesProps {
  baseUrl: string;
  store: ApplicationStoreType;
}
